import { createElement } from "react";
import { DateTime } from "luxon";
import { AgGrid } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { DefaultColumnTypes } from "#utils/agGrid.constants";
import { ActionButton } from "../../common/components/ActionButton";
import { actionButtonCellClass } from "../../common/components/ActionButton/styles_vanilla.css";
import { ClientFilter } from "../GridFilters/ClientFilter";
import { DateRangeFilter } from "../GridFilters/DateRangeFilter";
import { EntityFilter } from "../GridFilters/EntityFilter";
import { PaymentTypeFilter } from "../GridFilters/PaymentTypeFilter";
import Money from "../Money";
import { TransactionRowItem } from "./types";

/**
 * Converts a string to sentence case (first character uppercase, rest lowercase).
 * e.g. "Negative Contribution" → "Negative contribution", "Capital Out" → "Capital out"
 */
export const toSentenceCase = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * List of the supported columns in the Transactions table
 * (ordered left to right as specified in the LLD)
 */
export enum SupportedColumns {
  ClientName = "clientName",
  EntityName = "entityName",
  TransactionDate = "date",
  Type = "label",
  RequestedPaymentDate = "requestedPaymentDate",
  Description = "description",
  Positions = "payeeCount",
  TotalAmountPayable = "totalAmountPayable",
  RemainingToPayAmount = "remainingToPayAmount",
  Action = "action",
}

/**
 * The mapper provides the corresponding column name displayed in the table
 */
export const columnNames = new Map<SupportedColumns, string>([
  [SupportedColumns.ClientName, "Client"],
  [SupportedColumns.EntityName, "Entity"],
  [SupportedColumns.TransactionDate, "Transaction date"],
  [SupportedColumns.Type, "Type"],
  [SupportedColumns.RequestedPaymentDate, "Requested payment date"],
  [SupportedColumns.Description, "Description"],
  [SupportedColumns.Positions, "Positions"],
  [SupportedColumns.TotalAmountPayable, "Amount"],
  [SupportedColumns.RemainingToPayAmount, "Remaining to pay"],
  [SupportedColumns.Action, "Action"],
]);

export type ActionColumnCallbacks = {
  onPayClick?: (transaction: TransactionRowItem) => void;
  onReviewClick?: (transaction: TransactionRowItem) => void;
};

/**
 * Base column definitions without the action column
 */
const baseColumnDefinitions: AgGrid.ColDef[] = [
  {
    field: SupportedColumns.ClientName,
    headerName: columnNames.get(SupportedColumns.ClientName),
    filter: ClientFilter,
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
  },
  {
    field: SupportedColumns.EntityName,
    headerName: columnNames.get(SupportedColumns.EntityName),
    filter: EntityFilter,
    filterParams: {
      dependentFilter: SupportedColumns.ClientName,
    },
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
  },
  {
    field: SupportedColumns.TransactionDate,
    headerName: columnNames.get(SupportedColumns.TransactionDate),
    sortable: true,
    sort: "desc",
    filter: DateRangeFilter,
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
    valueFormatter: ({ data }: { data: TransactionRowItem }) => {
      return data?.date?.toLocaleString(DateTime.DATE_MED) ?? "";
    },
  },
  {
    field: SupportedColumns.Type,
    headerName: columnNames.get(SupportedColumns.Type),
    minWidth: 180,
    filter: PaymentTypeFilter,
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
    cellStyle: { display: "flex", alignItems: "center", overflow: "hidden" },
    cellRenderer: ({ data }: { data: TransactionRowItem }) => {
      if (!data) return null;
      const label = toSentenceCase(data.label);
      return createElement(
        "div",
        {
          style: { display: "flex", flexDirection: "column" },
        },
        createElement("span", { style: { lineHeight: "18px" } }, label),
        data.subtitleLabel
          ? createElement(
              "span",
              { style: { fontSize: "12px", color: Colors.Neutral600, lineHeight: "14px" } },
              `(${data.subtitleLabel})`,
            )
          : null,
      );
    },
    valueGetter: ({ data }: { data: TransactionRowItem }) => {
      if (!data) return "";
      const label = toSentenceCase(data.label);
      return data.subtitleLabel ? `${label} (${data.subtitleLabel})` : label;
    },
  },
  {
    field: SupportedColumns.RequestedPaymentDate,
    headerName: columnNames.get(SupportedColumns.RequestedPaymentDate),
    sortable: true,
    filter: DateRangeFilter,
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
    valueFormatter: ({ data }: { data: TransactionRowItem }) => {
      return data?.requestedPaymentDate?.toLocaleString(DateTime.DATE_MED) ?? "";
    },
  },
  {
    field: SupportedColumns.Description,
    headerName: columnNames.get(SupportedColumns.Description),
    minWidth: 200,
  },
  {
    field: SupportedColumns.Positions,
    headerName: columnNames.get(SupportedColumns.Positions),
    maxWidth: 130,
  },
  {
    field: SupportedColumns.TotalAmountPayable,
    headerName: columnNames.get(SupportedColumns.TotalAmountPayable),
    type: DefaultColumnTypes.numericColumn,
    maxWidth: 130,
    cellRenderer: Money,
    cellRendererParams: ({ data }: { data: TransactionRowItem }) => ({
      money: data?.totalAmountPayable,
      rounded: false,
    }),
  },
  {
    field: SupportedColumns.RemainingToPayAmount,
    headerName: columnNames.get(SupportedColumns.RemainingToPayAmount),
    type: DefaultColumnTypes.numericColumn,
    maxWidth: 200,
    resizable: false,
    cellRenderer: Money,
    cellRendererParams: ({ data }: { data: TransactionRowItem }) => ({
      money: data?.remainingToPayAmount,
      rounded: false,
    }),
  },
];

/**
 * Creates the action column definition with the provided callbacks
 */
const createActionColumnDef = (callbacks: ActionColumnCallbacks): AgGrid.ColDef => ({
  field: SupportedColumns.Action,
  headerName: columnNames.get(SupportedColumns.Action),
  pinned: "right",
  sortable: false,
  filter: false,
  resizable: false,
  flex: 0,
  width: 108,
  cellClass: actionButtonCellClass,
  cellRenderer: ActionButton,
  cellRendererParams: ({ data }: { data?: TransactionRowItem }) => {
    const action = data?.action ?? null;

    if (!data || !action) {
      return { action: null, onClick: undefined };
    }

    return {
      action,
      onClick: () => {
        if (action === "pay") callbacks.onPayClick?.(data);
        else callbacks.onReviewClick?.(data);
      },
    };
  },
});

/**
 * Creates AG Grid Column definitions for the Transactions table
 * @param callbacks - Optional callbacks for action button clicks
 */
export const getColumnDefinitions = (
  callbacks: ActionColumnCallbacks = {},
): AgGrid.ColDef[] => [...baseColumnDefinitions, createActionColumnDef(callbacks)];
